import { ref } from '@vue/composition-api';
import useSocket from '@/compositions/useSocket';
import { pushDebugLog } from '@/helpers';

const session = ref(null);
const keepAliveTimer = ref(null);
const CREATE_SESSION_TIMEOUT = 15000;

export default function useJanusSession() {
  const { sendEvent, registerMessageOnce, waitForConnection } = useSocket();

  function keepAlive() {
    clearInterval(keepAliveTimer.value);
    keepAliveTimer.value = setInterval(() => {
      sendEvent('session:keepalive', { sessionId: session.value.data.id });
    }, 5000);
  }

  function createSession() {
    return new Promise((resolve, reject) => {
      let resolved = false;
      let timeoutId = null;

      const rejectOnce = (error) => {
        if (resolved) {
          return;
        }

        resolved = true;
        clearTimeout(timeoutId);
        reject(error);
      };

      const resolveOnce = (data) => {
        if (resolved) {
          return;
        }

        resolved = true;
        clearTimeout(timeoutId);
        session.value = data;
        pushDebugLog('janus:session', 'session:created received', {
          sessionId: data?.data?.id,
        });
        resolve(data);
        keepAlive();
      };

      timeoutId = setTimeout(() => {
        if (resolved) {
          return;
        }

        pushDebugLog('janus:session', 'session:created timeout');
        rejectOnce(new Error('Janus session creation timed out'));
      }, CREATE_SESSION_TIMEOUT);

      registerMessageOnce('session:created', (data) => {
        resolveOnce(data);
      });

      registerMessageOnce('session:error', (errorData) => {
        pushDebugLog('janus:session', 'session:error received', {
          errorData,
        });
        rejectOnce(new Error(errorData?.message || 'Janus session error'));
      });

      waitForConnection()
        .then(() => {
          pushDebugLog('janus:session', 'session:create sent');
          sendEvent('session:create', {}, (ackData) => {
            pushDebugLog('janus:session', 'session:create ack received', {
              ackData,
            });

            if (!ackData) {
              return;
            }

            if (ackData.error) {
              rejectOnce(new Error(ackData.error?.message || ackData.error || 'Janus session ack error'));
              return;
            }

            if (ackData.data?.id || ackData.sessionId) {
              resolveOnce(ackData.data?.id ? ackData : { data: { id: ackData.sessionId } });
            }
          });
        })
        .catch((error) => {
          pushDebugLog('janus:session', 'waitForConnection failed', {
            errorMessage: error?.message,
          });
          rejectOnce(error);
        });
    });
  }

  return {
    session,
    createSession,
  };
}
